import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { PanelLeftOpen } from "lucide-react";
import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface AppShellContextValue {
  /** True while the sidebar is the icon rail (desktop) / hidden (phones). */
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

/**
 * Sidebar collapse state. Available to anything inside `<AppShell>` — the
 * header's menu button and the sidebar's own toggle both drive it.
 */
export function useAppShell(): AppShellContextValue {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShell must be called inside <AppShell>");
  }
  return context;
}

export interface AppShellProps extends HTMLAttributes<HTMLDivElement> {
  /** Uncontrolled initial state. Collapsed is the default on every viewport. */
  defaultCollapsed?: boolean;
  /** Controlled state. Pair with `onCollapsedChange`. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Target id for the skip link. Must match the `<AppContent>` id. */
  skipToId?: string;
  skipLabel?: string;
  children: ReactNode;
}

/**
 * Application shell: a floating overlay sidebar over a full-width content
 * column, with a transparent floating header above both.
 *
 * The sidebar never occupies layout flow. Collapsed it is an icon rail;
 * expanded it floats over the content behind a scrim, so opening the nav
 * never reflows the page underneath it. Escape or a pointer press outside
 * the panel closes it.
 */
export function AppShell({
  defaultCollapsed = true,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  skipToId = "app-main",
  skipLabel = "Skip to content",
  className,
  children,
  ...props
}: AppShellProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultCollapsed);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : uncontrolled;

  const setCollapsed = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolled(next);
      onCollapsedChange?.(next);
    },
    [isControlled, onCollapsedChange],
  );

  const context = useMemo<AppShellContextValue>(
    () => ({
      collapsed,
      setCollapsed,
      toggle: () => setCollapsed(!collapsed),
      open: () => setCollapsed(false),
      close: () => setCollapsed(true),
    }),
    [collapsed, setCollapsed],
  );

  // Dismissal while expanded. The sidebar itself, its body-portalled
  // section flyout, and the header's menu button are exempt — the button
  // owns its own toggle, and without the flyout exemption an outside-press
  // would close the panel before the chosen destination receives its click.
  useEffect(() => {
    if (collapsed) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".app-sidebar, .app-nav-popup, .app-menu-button")) return;
      setCollapsed(true);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCollapsed(true);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [collapsed, setCollapsed]);

  return (
    <AppShellContext.Provider value={context}>
      <div
        className={cn(
          "app-shell",
          collapsed && "app-shell--sidebar-collapsed",
          className,
        )}
        {...props}
      >
        <a href={`#${skipToId}`} className="app-skip-link">
          {skipLabel}
        </a>
        {collapsed ? null : (
          <button
            type="button"
            className="app-scrim"
            aria-label="Close navigation"
            onClick={() => setCollapsed(true)}
          />
        )}
        {children}
      </div>
    </AppShellContext.Provider>
  );
}

/** Phone-only control that opens the overlay sidebar. Hidden on wider viewports. */
export function AppShellMenuButton({ label = "Open navigation" }: { label?: string }) {
  const { open } = useAppShell();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="app-menu-button"
      aria-label={label}
      title={label}
      onClick={open}
    >
      <PanelLeftOpen className="size-4" aria-hidden="true" />
    </Button>
  );
}

export interface AppContentProps extends HTMLAttributes<HTMLElement> {
  /** Must match `<AppShell skipToId>`. */
  id?: string;
  children: ReactNode;
}

/**
 * Main content column. Clears the icon rail on the inline-start side and
 * centres an inner column in what is left; `.app-content`'s own top padding
 * reserves room for the floating header.
 */
export function AppContent({
  id = "app-main",
  className,
  children,
  ...props
}: AppContentProps) {
  return (
    <main id={id} className={cn("app-content", className)} {...props}>
      <div className="app-content-inner">{children}</div>
    </main>
  );
}
